import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAssistance, useChimeMeeting } from "../hooks";

export const MicrophoneContext = React.createContext({});

const MicrophoneProvider = ({ children }) => {
  const { assistance } = useAssistance();

  const microphones = useMemo(
    () => assistance?.available_speakers,
    [assistance],
  );

  const [selectedMicrophone, setSelectedMicrophone] = useState(
    microphones?.[0],
  );
  const [isMuted, setIsMuted] = useState(false);

  const { sendControlMessage, muteLocalAudio, unMuteLocalAudio } =
    useChimeMeeting();

  const isMicrophoneSelected = useCallback(
    (microphone) => {
      return microphone === selectedMicrophone;
    },
    [selectedMicrophone],
  );

  const unMuteMicrophone = useCallback(() => {
    if (isMuted) {
      setIsMuted(false);
      unMuteLocalAudio();
    }
  }, [isMuted, unMuteLocalAudio]);

  const onSelectMicrophone = useCallback(
    (speaker) => {
      unMuteMicrophone();
      setSelectedMicrophone(speaker);
    },
    [unMuteMicrophone],
  );

  const resetMicrophone = useCallback(() => {
    onSelectMicrophone(microphones?.[0]);
  }, [microphones, onSelectMicrophone]);

  const onMuteMicrophone = useCallback(() => {
    if (!isMuted) {
      setIsMuted(true);
      muteLocalAudio();
    }
  }, [isMuted, muteLocalAudio]);

  useEffect(() => {
    sendControlMessage(
      JSON.stringify({
        mic: selectedMicrophone?.name,
      }),
    );
  }, [selectedMicrophone, sendControlMessage]);

  const contextValue = useMemo(
    () => ({
      isMuted,
      onMuteMicrophone,
      unMuteMicrophone,
      resetMicrophone,
      selectedMicrophone,
      microphones,
      onSelectMicrophone,
      isMicrophoneSelected,
    }),
    [
      isMuted,
      onMuteMicrophone,
      unMuteMicrophone,
      resetMicrophone,
      selectedMicrophone,
      microphones,
      onSelectMicrophone,
      isMicrophoneSelected,
    ],
  );

  return (
    <MicrophoneContext.Provider value={contextValue}>
      {children}
    </MicrophoneContext.Provider>
  );
};

export default React.memo(MicrophoneProvider);
